import type { BackgroundCounts, ModelFilter } from './types';

export const getModelShortName = (model: string): string => {
    if (!model || model === 'unknown') return 'Unknown';
    if (model.includes('3.1-pro')) return '3.1 Pro';
    if (model.includes('3-pro') || model.includes('3.0-pro')) return '3.0 Pro';
    if (model.includes('2.5-pro')) return '2.5 Pro';
    if (model.includes('3-flash') || model.includes('3.0-flash')) return '3-Flash';
    if (model.includes('2.5-flash')) return '2.5-Flash';
    if (model.includes('flash')) return 'Flash';
    return model;
};

export const getModelBadgeVariant = (model: string): 'outline' | 'default' | 'secondary' | 'destructive' => {
    if (!model || model === 'unknown') return 'destructive';
    if (model.includes('3.1-pro') || model.includes('3-pro') || model.includes('3.0-pro')) return 'outline';
    if (model.includes('3-flash') || model.includes('3.0-flash')) return 'default';
    if (model.includes('2.5-flash')) return 'secondary';
    if (model.includes('2.5-pro')) return 'destructive';
    return 'destructive';
};

export const shouldShowBookModelBadge = (model?: string): boolean => {
    if (!model) return false;
    // Show if 3.1 Pro specifically (subtle indicator)
    if (model.includes('3.1-pro')) return true;
    return !(model.includes('3-pro') || model.includes('3.0-pro'));
};

export const shouldShowPassageModelBadges = (modelMap?: Record<string, number>): boolean => {
    if (!modelMap) return false;
    const models = Object.keys(modelMap);
    if (models.length === 0) return false;
    if (models.length === 1) {
        const onlyModel = models[0];
        // Show if 3.1 Pro specifically (subtle indicator)
        if (onlyModel.includes('3.1-pro')) return true;
        return !(onlyModel.includes('3-pro') || onlyModel.includes('3.0-pro'));
    }
    return true;
};

export const isPro = (model?: string) => {
    if (!model) return false;
    return model.includes('3.1-pro') || model.includes('3-pro') || model.includes('3.0-pro');
};

export const needsProUpgrade = (counts: BackgroundCounts): boolean => {
    const hasFlashOrPro25 = (modelMap?: Record<string, number>) => {
        if (!modelMap) return false;
        for (const model of Object.keys(modelMap)) {
            if (model.includes('flash') || model.includes('2.5-pro')) {
                return true;
            }
        }
        return false;
    };

    const isFlashOrPro25 = (model?: string) => {
        if (!model) return false;
        return model.includes('flash') || model.includes('2.5-pro');
    };

    return (
        hasFlashOrPro25(counts.culturalPassageByModel) ||
        isFlashOrPro25(counts.culturalBookModel) ||
        hasFlashOrPro25(counts.archaeologicalPassageByModel) ||
        isFlashOrPro25(counts.archaeologicalBookModel) ||
        hasFlashOrPro25(counts.historicalPassageByModel) ||
        isFlashOrPro25(counts.historicalBookModel) ||
        hasFlashOrPro25(counts.geographicByModel) ||
        hasFlashOrPro25(counts.peoplePassageByModel) ||
        hasFlashOrPro25(counts.literaryPassageByModel) ||
        isFlashOrPro25(counts.literaryBookModel) ||
        hasFlashOrPro25(counts.studyGuidePassageByModel)
    );
};

export const hasMixedModels = (counts: BackgroundCounts): boolean => {
    const checkMixed = (modelMap?: Record<string, number>) => {
        if (!modelMap) return false;
        return Object.keys(modelMap).length > 1;
    };

    return (
        checkMixed(counts.culturalPassageByModel) ||
        checkMixed(counts.archaeologicalPassageByModel) ||
        checkMixed(counts.historicalPassageByModel) ||
        checkMixed(counts.geographicByModel) ||
        checkMixed(counts.peoplePassageByModel) ||
        checkMixed(counts.literaryPassageByModel) ||
        checkMixed(counts.studyGuidePassageByModel)
    );
};

export const matchesModelFilter = (counts: BackgroundCounts, filter: ModelFilter): boolean => {
    if (filter === 'all') return true;
    if (filter === 'needs-upgrade') return needsProUpgrade(counts);
    if (filter === 'mixed') return hasMixedModels(counts);

    const hasFlashVersion = (version: '2.5' | '3', modelMap?: Record<string, number>) => {
        if (!modelMap) return false;
        for (const model of Object.keys(modelMap)) {
            if (version === '2.5' && model.includes('2.5-flash')) return true;
            if (version === '3' && (model.includes('3-flash') || model.includes('3.0-flash'))) return true;
        }
        return false;
    };

    if (filter === '2.5-flash') {
        return (
            hasFlashVersion('2.5', counts.culturalPassageByModel) ||
            counts.culturalBookModel?.includes('2.5-flash') ||
            hasFlashVersion('2.5', counts.archaeologicalPassageByModel) ||
            counts.archaeologicalBookModel?.includes('2.5-flash') ||
            hasFlashVersion('2.5', counts.historicalPassageByModel) ||
            counts.historicalBookModel?.includes('2.5-flash') ||
            hasFlashVersion('2.5', counts.geographicByModel) ||
            hasFlashVersion('2.5', counts.peoplePassageByModel) ||
            hasFlashVersion('2.5', counts.literaryPassageByModel) ||
            counts.literaryBookModel?.includes('2.5-flash') ||
            hasFlashVersion('2.5', counts.studyGuidePassageByModel)
        );
    }

    if (filter === '3-flash') {
        return (
            hasFlashVersion('3', counts.culturalPassageByModel) ||
            counts.culturalBookModel?.includes('3-flash') ||
            counts.culturalBookModel?.includes('3.0-flash') ||
            hasFlashVersion('3', counts.archaeologicalPassageByModel) ||
            counts.archaeologicalBookModel?.includes('3-flash') ||
            counts.archaeologicalBookModel?.includes('3.0-flash') ||
            hasFlashVersion('3', counts.historicalPassageByModel) ||
            counts.historicalBookModel?.includes('3-flash') ||
            counts.historicalBookModel?.includes('3.0-flash') ||
            hasFlashVersion('3', counts.geographicByModel) ||
            hasFlashVersion('3', counts.peoplePassageByModel) ||
            hasFlashVersion('3', counts.literaryPassageByModel) ||
            counts.literaryBookModel?.includes('3-flash') ||
            counts.literaryBookModel?.includes('3.0-flash') ||
            hasFlashVersion('3', counts.studyGuidePassageByModel)
        );
    }

    const hasFlashOrPro25 = needsProUpgrade(counts);
    if (filter === 'pro') return !hasFlashOrPro25;

    return true;
};

export const calculateProPercentage = (counts: BackgroundCounts): number => {
    let proItems = 0;
    const totalPassages = counts.totalPassages || 0;

    const getProCount = (modelMap?: Record<string, number>) => {
        if (!modelMap) return 0;
        let pro = 0;
        for (const [model, count] of Object.entries(modelMap)) {
            if (isPro(model)) {
                pro += count;
            }
        }
        return pro;
    };

    // 1. Passage-level datasets (6 datasets)
    proItems += getProCount(counts.literaryPassageByModel);
    proItems += getProCount(counts.culturalPassageByModel);
    proItems += getProCount(counts.archaeologicalPassageByModel);
    proItems += getProCount(counts.historicalPassageByModel);
    proItems += getProCount(counts.geographicByModel);
    proItems += getProCount(counts.peoplePassageByModel);
    proItems += getProCount(counts.studyGuidePassageByModel);

    // 2. Book-level datasets (4 datasets)
    if (counts.literaryBook > 0 && isPro(counts.literaryBookModel || '')) proItems++;
    if (counts.culturalBook > 0 && isPro(counts.culturalBookModel || '')) proItems++;
    if (counts.archaeologicalBook > 0 && isPro(counts.archaeologicalBookModel || '')) proItems++;
    if (counts.historicalBook > 0 && isPro(counts.historicalBookModel || '')) proItems++;

    // Total expected items across all pro datasets
    // (7 passage datasets * N passages) + (4 book-level datasets)
    const expectedTotal = (7 * totalPassages) + 4;

    if (expectedTotal === 0) return 0;
    return Math.floor((proItems / expectedTotal) * 100);
};

export const isBookComplete = (counts: BackgroundCounts): boolean => {
    return counts.literaryBook > 0 &&
        counts.literaryPassage === counts.totalPassages &&
        counts.culturalBook > 0 &&
        counts.culturalPassage === counts.totalPassages &&
        counts.archaeologicalBook > 0 &&
        counts.archaeologicalPassage === counts.totalPassages &&
        counts.historicalBook > 0 &&
        counts.historicalPassage === counts.totalPassages &&
        counts.geographic === counts.totalPassages &&
        counts.peoplePassage === counts.totalPassages &&
        counts.studyGuidePassage === counts.totalPassages;
};

export const isBookEmpty = (counts: BackgroundCounts): boolean => {
    return counts.literaryBook === 0 &&
        counts.literaryPassage === 0 &&
        counts.culturalBook === 0 &&
        counts.culturalPassage === 0 &&
        counts.archaeologicalBook === 0 &&
        counts.archaeologicalPassage === 0 &&
        counts.historicalBook === 0 &&
        counts.historicalPassage === 0 &&
        counts.geographic === 0 &&
        counts.peoplePassage === 0 &&
        counts.studyGuidePassage === 0 &&
        counts.guidedStudyPassage === 0;
};

export const getBookCompletionPercentage = (counts: BackgroundCounts): number => {
    const total = 11; // 4 book-level, 7 passage-level
    let completed = 0;

    if (counts.literaryBook > 0) completed++;
    if (counts.literaryPassage === counts.totalPassages && counts.totalPassages > 0) completed++;
    else if (counts.literaryPassage > 0 && counts.totalPassages > 0) completed += counts.literaryPassage / counts.totalPassages;

    if (counts.culturalBook > 0) completed++;
    if (counts.culturalPassage === counts.totalPassages && counts.totalPassages > 0) completed++;
    else if (counts.culturalPassage > 0 && counts.totalPassages > 0) completed += counts.culturalPassage / counts.totalPassages;

    if (counts.archaeologicalBook > 0) completed++;
    if (counts.archaeologicalPassage === counts.totalPassages && counts.totalPassages > 0) completed++;
    else if (counts.archaeologicalPassage > 0 && counts.totalPassages > 0) completed += counts.archaeologicalPassage / counts.totalPassages;

    if (counts.historicalBook > 0) completed++;
    if (counts.historicalPassage === counts.totalPassages && counts.totalPassages > 0) completed++;
    else if (counts.historicalPassage > 0 && counts.totalPassages > 0) completed += counts.historicalPassage / counts.totalPassages;

    if (counts.geographic === counts.totalPassages && counts.totalPassages > 0) completed++;
    else if (counts.geographic > 0 && counts.totalPassages > 0) completed += counts.geographic / counts.totalPassages;

    if (counts.peoplePassage === counts.totalPassages && counts.totalPassages > 0) completed++;
    else if (counts.peoplePassage > 0 && counts.totalPassages > 0) completed += counts.peoplePassage / counts.totalPassages;

    if (counts.studyGuidePassage === counts.totalPassages && counts.totalPassages > 0) completed++;
    else if (counts.studyGuidePassage > 0 && counts.totalPassages > 0) completed += counts.studyGuidePassage / counts.totalPassages;

    return (completed / total) * 100;
};
